import React, { useMemo } from 'react';

/** Google-style search snippet preview (iter-13).
 *
 *  Re-creates Google's mobile + desktop result layout using only Tailwind:
 *  - Top row: favicon (16px round-square) · site name · breadcrumb URL
 *  - Title: blue (#1a0dab) ~20px, truncated at 60 chars (Google's snippet cap)
 *  - Description: gray (#4d5156) ~14px, truncated at 155 chars
 *
 *  Pulled into its own file so the SEO panel stays focused on the editor. */
export default function SeoSnippetPreview({ siteUrl, route, title, description, noIndex }) {
  const { displayTitle, displayDesc, breadcrumb, host } = useMemo(() => {
    const fallbackTitle = 'The Loom V2.38_f55 — Multi-Model AI for Long-Form Novel Writing';
    const fallbackDesc = (
      'Draft, refine, and ship novels with Claude, GPT, and Gemini. ' +
      'Character bibles, editable variables, chapter version history.'
    );
    const t = (title || '').trim() || fallbackTitle;
    const d = (description || '').trim() || fallbackDesc;
    const trimTitle = t.length > 60 ? t.slice(0, 57).trimEnd() + '…' : t;
    const trimDesc = d.length > 155 ? d.slice(0, 152).trimEnd() + '…' : d;
    let h = '';
    try { h = new URL(siteUrl || 'https://example.com').host; }
    catch { h = (siteUrl || '').replace(/^https?:\/\//, '').split('/')[0]; }
    const crumb = (route || '/').replace(/^\//, '').split('/').filter(Boolean);
    const trail = crumb.length === 0 ? '' : ' › ' + crumb.join(' › ');
    return { displayTitle: trimTitle, displayDesc: trimDesc, breadcrumb: trail, host: h };
  }, [siteUrl, route, title, description]);

  return (
    <div
      className="mt-4 rounded-md border border-border bg-background p-4"
      data-testid="seo-snippet-preview"
    >
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-semibold text-foreground">Search-result preview</div>
        {noIndex && (
          <span className="rounded-sm border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[10px] font-mono-stat text-warning-foreground">
            no-index — hidden from search
          </span>
        )}
      </div>
      <p className="text-[10.5px] text-muted-foreground">
        How this page will appear on a Google SERP. Snippet uses live editor values, not the saved record.
      </p>

      {/* Card: white background with a soft border to mimic Google's result block */}
      <div className="mt-3 rounded-lg border border-[#dadce0] bg-white p-4 dark:border-[#3c4043] dark:bg-[#202124]">
        {/* Source row — favicon + site + breadcrumb */}
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#f1f3f4] dark:bg-[#3c4043]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="3" fill="#c2410c" />
              <text
                x="12"
                y="16"
                textAnchor="middle"
                fontSize="11"
                fontWeight="700"
                fill="#fff"
                fontFamily="system-ui, sans-serif"
              >P</text>
            </svg>
          </div>
          <div className="flex flex-col leading-tight">
            <div className="text-[12px] font-medium text-[#202124] dark:text-[#e8eaed]">The Loom V2.38_f55</div>
            <div className="text-[11px] text-[#5f6368] dark:text-[#9aa0a6]">
              {host}{breadcrumb}
            </div>
          </div>
        </div>

        {/* Title — Google's iconic Arial blue */}
        <div
          className="mt-2 text-[18px] leading-snug text-[#1a0dab] visited:text-[#681da8] dark:text-[#8ab4f8]"
          style={{ fontFamily: 'Arial, sans-serif' }}
        >
          {displayTitle}
        </div>

        {/* Description — Google's grey body text */}
        <p
          className="mt-1 text-[13px] leading-snug text-[#4d5156] dark:text-[#bdc1c6]"
          style={{ fontFamily: 'Arial, sans-serif' }}
        >
          {displayDesc}
        </p>
      </div>

      <div className="mt-2 flex flex-wrap gap-2 text-[10.5px] text-muted-foreground">
        <span data-testid="seo-snippet-title-len">Title {title.length}/60</span>
        <span>·</span>
        <span data-testid="seo-snippet-desc-len">Description {description.length}/155</span>
      </div>
    </div>
  );
}
